/**
 * The brand lockup, in one place.
 *
 * The header and the hero used to set the name two different ways — the header
 * in a serif "Davis's Reel" plus a tiny letterspaced sans "Thoughts", the hero
 * in one serif stack with the accent on the wrong word. Two treatments of the
 * same three words read as two brands, so both now render this component and
 * differ only in scale and in how many words they use.
 *
 * The rule the accent follows: **Reel is gold, everything else is white.** It
 * is the only word in the name that means film, and it is the reel the whole
 * site is built around.
 */

type Size = "sm" | "md" | "hero" | "heroStack";

interface WordmarkProps {
  size?: Size;
  /**
   * `full` is the whole name on one line, for the header and footer.
   * `stacked` is the whole name over three lines, for the hero.
   * `short` drops the possessive and stacks the two remaining words.
   */
  words?: "full" | "stacked" | "short";
  className?: string;
}

const TYPE: Record<Size, string> = {
  sm: "text-xl",
  md: "text-2xl",
  // Sized to fill the viewport width and crowd its own margins.
  hero: "text-[length:var(--text-mega)]",
  /**
   * The whole name, stacked over three lines.
   *
   * Smaller than `hero` because it has to be: three lines of the mega size
   * would be taller than the pinned hero panel, and "Thoughts" is the widest
   * word in the lockup, so the ceiling is set by fitting that on one line
   * rather than by how big the type could theoretically go.
   */
  heroStack: "text-[length:var(--text-mega-stack)]",
};

/**
 * Tight leading and a negative tracking are what make the hero read as a poster
 * rather than as a heading. At small sizes that same tracking closes the
 * letters up too far, so only the hero takes the full amount.
 */
const SPACING: Record<Size, string> = {
  sm: "leading-none tracking-[-0.02em]",
  md: "leading-tight tracking-[-0.025em]",
  hero: "leading-[0.82] tracking-[-0.035em]",
  heroStack: "leading-[0.84] tracking-[-0.035em]",
};

export function Wordmark({
  size = "sm",
  words = "full",
  className = "",
}: WordmarkProps) {
  return (
    <span
      className={`font-display text-paper-50 ${TYPE[size]} ${SPACING[size]} ${className}`}
    >
      {/*
        Plain text nodes with one span around the accent word, rather than a
        span per word: a screen reader still announces the whole name as one
        phrase instead of three separate utterances.
      */}
      {words === "stacked" ? (
        <>
          Davis's
          <br />
          <span className="text-lamp-400">Reel</span>
          <br />
          Thoughts
        </>
      ) : words === "full" ? (
        <>
          Davis's <span className="text-lamp-400">Reel</span> Thoughts
        </>
      ) : (
        <>
          <span className="text-lamp-400">Reel</span>
          <br />
          Thoughts
        </>
      )}
    </span>
  );
}
